import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { DarkTiles } from "@/components/map/DarkTiles";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// === Helpers ===

// Haversine distance in km
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Bearing (north-clockwise degrees) from A to B — used for rotating the courier icon
function bearingBetween(lat1, lon1, lat2, lon2) {
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
}

// Find the index of the closest point on a polyline to a given lat/lng
function closestPointIndex(polyline, lat, lng) {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < polyline.length; i++) {
    const d = haversineDistance(polyline[i][0], polyline[i][1], lat, lng);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

// === Animated courier icon (rotation + pulse) ===

function buildCourierIcon(headingDeg = 0) {
  return L.divIcon({
    className: 'arteknos-courier-marker',
    html: `<div class="ak-courier-pulse-wrap">
      <div class="ak-courier-pulse-ring"></div>
      <div class="ak-courier-pulse-ring ak-courier-pulse-ring-2"></div>
      <div class="ak-courier-inner" style="transform: rotate(${headingDeg}deg);">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="22" height="22">
          <path d="M12 2L4 14h5v8h6v-8h5L12 2z"/>
        </svg>
      </div>
    </div>`,
    iconSize: [56, 56],
    iconAnchor: [28, 28],
    popupAnchor: [0, -28]
  });
}

// Pickup icon (royal blue)
const pickupIcon = L.divIcon({
  className: 'arteknos-pickup-icon',
  html: `<div class="ak-pin ak-pin-pickup">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="20" height="20">
      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    </svg>
  </div>`,
  iconSize: [40, 50],
  iconAnchor: [20, 48],
  popupAnchor: [0, -48]
});

// Delivery icon (cyan)
const deliveryIcon = L.divIcon({
  className: 'arteknos-delivery-icon',
  html: `<div class="ak-pin ak-pin-delivery">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="20" height="20">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [40, 50],
  iconAnchor: [20, 48],
  popupAnchor: [0, -48]
});

// === Fit bounds once on load ===
function AutoFitBounds({ pickupLocation, deliveryLocation, courierPosition, route }) {
  const map = useMap();
  const hasFitted = useRef(false);

  useEffect(() => {
    if (hasFitted.current) return;
    const points = [];
    if (pickupLocation?.lat) points.push([pickupLocation.lat, pickupLocation.lng]);
    if (deliveryLocation?.lat) points.push([deliveryLocation.lat, deliveryLocation.lng]);
    if (courierPosition) points.push(courierPosition);
    if (route && route.length > 0) points.push(...route);
    if (points.length >= 2) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 16 });
      hasFitted.current = true;
    }
  }, [pickupLocation, deliveryLocation, courierPosition, route, map]);
  return null;
}

// === Suivi type navigation : recentre + zoom rue sur le livreur toutes les 2 s ===
const FOLLOW_ZOOM = 17;
function SmoothFollow({ position, shouldFollow, onUserMove }) {
  const map = useMap();
  const posRef = useRef(position);
  posRef.current = position;
  useEffect(() => {
    if (!shouldFollow) return undefined;
    const tick = () => {
      if (posRef.current) map.setView(posRef.current, FOLLOW_ZOOM, { animate: true, duration: 0.8 });
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, [shouldFollow, map]);
  useEffect(() => {
    const stop = () => onUserMove?.();
    map.on("dragstart", stop);
    return () => map.off("dragstart", stop);
  }, [map, onUserMove]);
  return null;
}

// === Main component ===
export default function LiveTrackingMap({
  orderId,
  pickupLocation,
  deliveryLocation,
  courierLocation,
  courierName,
  orderStatus,
  height = '500px',
  onCourierLocationUpdate,
}) {
  const [rawCourier, setRawCourier] = useState(courierLocation);          // last received GPS
  const [animatedCourier, setAnimatedCourier] = useState(courierLocation); // smooth interpolated marker pos
  const [heading, setHeading] = useState(0);
  const [route, setRoute] = useState([]);                                  // full road-aligned polyline
  const [followCourier, setFollowCourier] = useState(true);
  const stopFollow = useCallback(() => setFollowCourier(false), []);
  const [eta, setEta] = useState(null);
  const [distanceRemaining, setDistanceRemaining] = useState(null);
  const [progressPct, setProgressPct] = useState(0);

  const pollingRef = useRef(null);
  const animFrameRef = useRef(null);
  const animStartRef = useRef(null);

  const dakarCenter = [14.6928, -17.4467];
  const isAssigned = orderStatus === 'assigned';
  const isInTransit = ['picked_up', 'in_transit'].includes(orderStatus);
  const isLive = ['assigned', 'picked_up', 'in_transit'].includes(orderStatus);

  // Target the right destination depending on status
  const target = useMemo(() => {
    if (isAssigned) return pickupLocation;
    if (isInTransit) return deliveryLocation;
    return null;
  }, [isAssigned, isInTransit, pickupLocation, deliveryLocation]);

  // === Fetch real road route via OSRM (public demo server) ===
  useEffect(() => {
    if (!rawCourier || !target?.lat || !target?.lng) {
      setRoute([]);
      return;
    }
    const ctrl = new AbortController();
    const url = `https://router.project-osrm.org/route/v1/driving/${rawCourier[1]},${rawCourier[0]};${target.lng},${target.lat}?overview=full&geometries=geojson`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.routes?.[0]?.geometry?.coordinates) return;
        const coords = data.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]);
        setRoute(coords);
      })
      .catch(() => {
        // Silently fall back to straight line — OSRM may fail offline
      });
    return () => ctrl.abort();
    // We refetch only when status pivots (target changes) or when courier moves >500m
    // — to avoid hammering OSRM at every GPS ping
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.lat, target?.lng, orderStatus]);

  // === Smoothly interpolate marker between two GPS pings (1.5s easing) ===
  useEffect(() => {
    if (!rawCourier) return;
    if (!animatedCourier) {
      setAnimatedCourier(rawCourier);
      return;
    }
    const from = animatedCourier;
    const to = rawCourier;
    if (Math.abs(from[0] - to[0]) < 1e-7 && Math.abs(from[1] - to[1]) < 1e-7) return;

    const newHeading = bearingBetween(from[0], from[1], to[0], to[1]);
    setHeading(newHeading);

    const duration = 1500;
    animStartRef.current = null;
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

    const step = (ts) => {
      if (!animStartRef.current) animStartRef.current = ts;
      const elapsed = ts - animStartRef.current;
      const t = Math.min(1, elapsed / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const lat = from[0] + (to[0] - from[0]) * eased;
      const lng = from[1] + (to[1] - from[1]) * eased;
      setAnimatedCourier([lat, lng]);
      if (t < 1) animFrameRef.current = requestAnimationFrame(step);
    };
    animFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawCourier]);

  // === Calculate ETA + remaining distance + route progress ===
  useEffect(() => {
    if (!animatedCourier || !target?.lat) {
      setEta(null);
      setDistanceRemaining(null);
      setProgressPct(0);
      return;
    }
    const dist = haversineDistance(animatedCourier[0], animatedCourier[1], target.lat, target.lng);
    setDistanceRemaining(dist);
    const avgSpeed = 25; // km/h Dakar urban traffic
    setEta(Math.max(1, Math.round((dist / avgSpeed) * 60)));

    if (route.length > 1) {
      const idx = closestPointIndex(route, animatedCourier[0], animatedCourier[1]);
      const pct = Math.min(99, Math.round((idx / (route.length - 1)) * 100));
      setProgressPct(pct);
    }
  }, [animatedCourier, target, route]);

  // === Poll backend for latest courier GPS ===
  useEffect(() => {
    if (!orderId || !isLive) return;

    const poll = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/courier-location`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.latitude && data.longitude) {
          setRawCourier([data.latitude, data.longitude]);
          if (onCourierLocationUpdate) onCourierLocationUpdate(data);
        }
      } catch {
        /* ignore network errors */
      }
    };
    poll();
    pollingRef.current = setInterval(poll, 6000);
    return () => clearInterval(pollingRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, isLive]);

  // External courierLocation prop sync
  useEffect(() => {
    if (courierLocation) setRawCourier(courierLocation);
  }, [courierLocation]);

  // === Split route into "covered" (faded) and "remaining" (vivid) ===
  const { coveredRoute, remainingRoute } = useMemo(() => {
    if (route.length < 2 || !animatedCourier) {
      return { coveredRoute: [], remainingRoute: route };
    }
    const idx = closestPointIndex(route, animatedCourier[0], animatedCourier[1]);
    return {
      coveredRoute: route.slice(0, idx + 1),
      remainingRoute: route.slice(idx),
    };
  }, [route, animatedCourier]);

  const center = useMemo(() => {
    if (animatedCourier && followCourier) return animatedCourier;
    if (pickupLocation?.lat) return [pickupLocation.lat, pickupLocation.lng];
    return dakarCenter;
  }, [animatedCourier, followCourier, pickupLocation]);

  const courierIcon = useMemo(() => buildCourierIcon(heading), [heading]);

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-2xl"
      style={{ height }}
      data-testid="live-tracking-map"
    >
      {/* Top floating ETA pill */}
      {eta !== null && isLive && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-black/85 backdrop-blur-md rounded-full px-5 py-2.5 shadow-2xl border border-[#D4AF37]/40"
          data-testid="eta-banner"
        >
          <div className="flex items-center gap-3 text-white">
            <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#003594] to-[#D4AF37] flex items-center justify-center">
              <span className="absolute inset-0 rounded-full ring-2 ring-[#D4AF37] animate-ping opacity-60"></span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="18" height="18" className="relative">
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" />
              </svg>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-white/60 font-semibold">
                {isAssigned ? 'Arrivée du livreur' : 'Arrivée chez vous'}
              </p>
              <p className="text-lg font-black leading-tight" data-testid="eta-value">
                {eta <= 1 ? '< 1 min' : `~${eta} min`}
              </p>
            </div>
            <div className="border-l border-white/20 pl-3">
              <p className="text-[10px] uppercase tracking-wide text-white/60">Distance</p>
              <p className="text-sm font-bold">{distanceRemaining?.toFixed(1)} km</p>
            </div>
          </div>
          {/* Progress bar */}
          {route.length > 1 && progressPct > 0 && (
            <div className="mt-2 h-1 bg-white/15 rounded-full overflow-hidden" data-testid="route-progress">
              <div
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#003594] transition-all duration-1000"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Follow toggle */}
      {animatedCourier && (
        <button
          onClick={() => setFollowCourier(!followCourier)}
          className={`absolute top-4 right-4 z-[1000] px-4 py-2.5 rounded-full text-sm font-semibold shadow-xl transition-all border-2 ${
            followCourier
              ? 'bg-[#003594] text-white border-[#D4AF37]'
              : 'bg-white/95 text-gray-700 border-gray-200 hover:border-[#003594]'
          }`}
          data-testid="follow-courier-btn"
        >
          {followCourier ? '🎯 Suivi actif' : '📍 Suivre livreur'}
        </button>
      )}

      {/* Status badge */}
      <div
        className="absolute bottom-4 left-4 z-[1000] bg-black/85 backdrop-blur-md rounded-2xl px-4 py-3 shadow-2xl border border-white/10"
        data-testid="status-badge"
      >
        <div className="flex items-center gap-2 text-white">
          {isInTransit && (
            <>
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#D4AF37]"></span>
              </span>
              <span className="text-sm font-semibold">En livraison</span>
            </>
          )}
          {isAssigned && (
            <>
              <span className="w-3 h-3 bg-[#003594] rounded-full animate-pulse"></span>
              <span className="text-sm font-semibold">Livreur en route vers le retrait</span>
            </>
          )}
          {orderStatus === 'delivered' && (
            <>
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="text-sm font-semibold text-green-300">Livré ✓</span>
            </>
          )}
        </div>
        {courierName && (
          <p className="text-[11px] text-white/60 mt-1">Livreur : {courierName}</p>
        )}
      </div>

      {/* Map */}
      <MapContainer
        center={center}
        zoom={14}
        style={{ height: '100%', width: '100%', background: '#0a0a0a' }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <DarkTiles />

        <AutoFitBounds
          pickupLocation={pickupLocation}
          deliveryLocation={deliveryLocation}
          courierPosition={animatedCourier}
          route={route}
        />
        <SmoothFollow position={animatedCourier} shouldFollow={followCourier} onUserMove={stopFollow} />

        {/* Covered (behind) */}
        {coveredRoute.length > 1 && (
          <Polyline positions={coveredRoute} color="#D4AF37" weight={6} opacity={0.3} />
        )}
        {/* Remaining (ahead) — vivid royal blue with cyan glow */}
        {remainingRoute.length > 1 && (
          <>
            <Polyline positions={remainingRoute} color="#D4AF37" weight={10} opacity={0.18} />
            <Polyline positions={remainingRoute} color="#003594" weight={5} opacity={0.95} lineCap="round" lineJoin="round" />
          </>
        )}

        {/* Fallback straight line if OSRM unavailable */}
        {route.length === 0 && animatedCourier && target?.lat && (
          <Polyline
            positions={[animatedCourier, [target.lat, target.lng]]}
            color="#D4AF37"
            weight={4}
            opacity={0.6}
            dashArray="8, 12"
          />
        )}

        {/* Pickup marker */}
        {pickupLocation?.lat && pickupLocation?.lng && (
          <Marker position={[pickupLocation.lat, pickupLocation.lng]} icon={pickupIcon}>
            <Popup>
              <div className="text-center p-1">
                <strong className="text-[#003594]">Point de retrait</strong>
                <p className="text-sm mt-1">{pickupLocation.address || 'Adresse de retrait'}</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Delivery marker */}
        {deliveryLocation?.lat && deliveryLocation?.lng && (
          <Marker position={[deliveryLocation.lat, deliveryLocation.lng]} icon={deliveryIcon}>
            <Popup>
              <div className="text-center p-1">
                <strong className="text-[#D4AF37]">Destination</strong>
                <p className="text-sm mt-1">{deliveryLocation.address || 'Adresse de livraison'}</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Courier marker (animated, rotated) */}
        {animatedCourier && (
          <Marker position={animatedCourier} icon={courierIcon} zIndexOffset={1000}>
            <Popup>
              <div className="text-center p-1">
                <strong className="text-[#003594]">{courierName || 'Votre livreur'}</strong>
                <p className="text-xs text-gray-500 mt-1">Position temps réel</p>
                {eta && <p className="text-xs font-bold text-[#D4AF37] mt-1">ETA : ~{eta} min</p>}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* CSS — animated marker, road-aligned glow */}
      <style>{`
        .ak-courier-pulse-wrap {
          position: relative;
          width: 56px;
          height: 56px;
        }
        .ak-courier-pulse-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 3px solid #D4AF37;
          animation: ak-pulse-ring 2.2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
          box-shadow: 0 0 12px rgba(0, 184, 225, 0.6);
        }
        .ak-courier-pulse-ring-2 {
          animation-delay: 1.1s;
          border-color: #003594;
        }
        .ak-courier-inner {
          position: absolute;
          top: 8px; left: 8px;
          width: 40px; height: 40px;
          background: linear-gradient(135deg, #003594, #D4AF37);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 6px 20px rgba(0, 71, 171, 0.7);
          transition: transform 0.6s cubic-bezier(0.215, 0.61, 0.355, 1);
        }
        @keyframes ak-pulse-ring {
          0% { transform: scale(0.7); opacity: 0.9; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        .ak-pin {
          width: 40px;
          height: 50px;
          position: relative;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 6px;
          background: transparent;
        }
        .ak-pin::before {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 40px; height: 40px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: 0 6px 18px rgba(0,0,0,0.35);
          z-index: 1;
        }
        .ak-pin > svg { position: relative; z-index: 2; }
        .ak-pin-pickup::before { background: linear-gradient(135deg, #003594, #003380); }
        .ak-pin-delivery::before { background: linear-gradient(135deg, #D4AF37, #B8941F); }
      `}</style>
    </div>
  );
}
